// src/encounter/lib/referral-practitioners.ts
// [PROD-1395 / PROD-3336] The practitioners a referral can name: the org's practitioners who hold the referred SPECIALITY
// (its id in their `doctorSpecialityIds`, or its name among their speciality names). The PROD-1395 dietitian target, which
// has no speciality id, keeps matching by the dietetics discipline (allied-discipline.ts). PURE.
import type { DoctorList } from '@/types/settings-api';
import { alliedDisciplineOf } from './allied-discipline';
import { isLegacyDietitian, type ReferralSpeciality } from './referral-specialities';

export interface ReferralPractitioner {
  id: string;
  name: string;
}

/** The speciality display names of one practitioner (the main speciality and every listed speciality). */
function specialityNames(d: DoctorList): string[] {
  return [d.speciality?.code?.displayName, ...(d.doctorSpecialities ?? []).map((s) => s.code?.displayName)].filter(
    (n): n is string => !!n,
  );
}

const norm = (s: string) => s.trim().toLowerCase();

function holds(d: DoctorList, speciality: Pick<ReferralSpeciality, 'id' | 'name'>): boolean {
  if (isLegacyDietitian(speciality.id)) return specialityNames(d).some((n) => alliedDisciplineOf(n) === 'dietetics');
  const id = norm(speciality.id);
  if ((d.doctorSpecialityIds ?? []).some((x) => norm(x) === id)) return true;
  // A row without ids (an older list shape) still matches on the speciality's name.
  return specialityNames(d).some((n) => norm(n) === norm(speciality.name));
}

/** The practitioners who hold the speciality, as picker rows. */
export function practitionersOfSpeciality(
  doctors: readonly DoctorList[],
  speciality: Pick<ReferralSpeciality, 'id' | 'name'>,
): ReferralPractitioner[] {
  return doctors
    .filter((d) => d.doctorSetupId && holds(d, speciality))
    .map((d) => ({
      id: d.doctorSetupId as string,
      name: `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim() || 'Practitioner',
    }));
}
